/**
 * Grouped catalog view: fold a filtered + sorted item list into question cards (categorical markets) and standalone
 * tiles. A question card takes the position of its first member in the sorted list, so every sort still applies.
 * Pure; question titles/labels come from describe/.
 */
import { RULES, cardLabel, type Rules } from '../classify';
import type { OutcomeDescription, QuestionDescription } from '../describe';
import type { QuestionSpec } from '../hl/types';
import { questionDayVolume, type DayVolume } from '../volume/volume';
import type { CatalogItem, SortKey } from './catalog';
import type { QuestionCardData } from './QuestionCard';

export type CatalogEntry = { kind: 'tile'; item: CatalogItem } | { kind: 'question'; group: QuestionCardData };

export interface GroupDeps {
  questions: readonly QuestionSpec[];
  describeQuestion: (q: QuestionSpec) => QuestionDescription;
  describeOutcome: (id: number) => OutcomeDescription | undefined;
  /** 24 h volume by outcome (#874): a card shows the sum over the question's named outcomes. */
  dayVolumes?: ReadonlyMap<number, DayVolume>;
  /** Question tags (#875, classified from the question alone) for the card label. */
  questionTags?: (q: QuestionSpec) => readonly string[];
  /** Rules in use for the card label (default: bundled). */
  rules?: Rules;
}

/** Outcome rows a question shows before "Show all". */
export const MAX_QUESTION_ROWS = 8;

/**
 * A question's named outcomes in display order: highest Yes price first, unpriced last (like trade.xyz / outcome.xyz
 * question cards); most traded first when the catalog is sorted by volume (#874).
 */
export function questionRows(group: QuestionCardData, sort: SortKey | undefined): QuestionCardData['rows'] {
  return sort === 'vol'
    ? [...group.rows].sort((a, b) => (b.item.volume?.usd ?? -1) - (a.item.volume?.usd ?? -1))
    : [...group.rows].sort((a, b) => (b.item.mid ?? -1) - (a.item.mid ?? -1));
}

export function toEntries(items: readonly CatalogItem[], view: 'flat' | 'grouped', deps: GroupDeps): CatalogEntry[] {
  if (view === 'flat') return items.map((item) => ({ kind: 'tile', item }));
  const qById = new Map(deps.questions.map((q) => [q.question, q]));
  const groups = new Map<number, QuestionCardData>();
  const out: CatalogEntry[] = [];
  for (const item of items) {
    const q = item.question !== undefined ? qById.get(item.question) : undefined;
    if (!q) {
      out.push({ kind: 'tile', item });
      continue;
    }
    let g = groups.get(q.question);
    if (!g) {
      const qd = deps.describeQuestion(q);
      g = {
        question: q.question,
        title: qd.title,
        subtitle: qd.subtitle,
        category: qd.category,
        resolvesAt: qd.resolvesAt,
        resolvesLabel: qd.resolvesLabel,
        rows: [],
        settled: (q.settledNamedOutcomes ?? []).length,
        tagLabel: cardLabel(deps.rules ?? RULES, deps.questionTags?.(q) ?? []) ?? undefined,
      };
      const vol = deps.dayVolumes ? questionDayVolume(deps.dayVolumes, q.namedOutcomes ?? []) : undefined;
      if (vol) g.volume = vol;
      groups.set(q.question, g);
      out.push({ kind: 'question', group: g });
    }
    if ((q.settledNamedOutcomes ?? []).includes(item.outcome)) continue; // settled: hidden (counted above)
    const label = deps.describeOutcome(item.outcome)?.label ?? item.title;
    if (item.outcome === q.fallbackOutcome) g.fallback = { item, label };
    else g.rows.push({ item, label });
  }
  return out;
}
